/* eslint-disable indent */
import type { FC } from 'react'

import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useRef, useState, useEffect } from 'react'
import { useWindowScroll, useWindowSize, useScrollbarWidth } from 'react-use'
import styled, { css } from 'styled-components'

interface AffixWrapperProps {
  affix?: boolean
  offset?: number
  height?: number
  width?: number
  top?: number
  leftOffset?: number
  forceTop?: number
}

export const AffixContainer = styled.div<AffixWrapperProps>``

export const AffixWrapper = styled.div<AffixWrapperProps>`
  height: ${({ affix, height }) => (affix ? `${height}px` : 'auto')};

  ${AffixContainer} {
    ${({ affix, leftOffset }) =>
      affix
        ? css`
            left: 50%;
            margin-left: ${leftOffset}px;
          `
        : css``}
    width: ${({ width }) => (width ? `${width}px` : 'inherit')};
    top: ${({ offset }) => (offset ? `${offset}px` : 'inherit')};

    // if forceTop are defined, the set override top
    // ${({ forceTop, offset }) => forceTop !== undefined && `top: ${(offset || 0) + forceTop}px`};
  }

  transition: all 0.3s ease-in-out;

  .affix-active {
    position: fixed;
  }
`

interface AffixProps {
  offset?: number
  enabled?: boolean
  idleClassName?: string
  activeClassName?: string
  target?: string
  scrollableAreaSelector?: string
}

const Affix: FC<AffixProps> = ({ children, ...props }) => {
  const {
    offset = 0,
    enabled = true,
    idleClassName = 'affix',
    activeClassName = 'affix-active',
    scrollableAreaSelector,
    target,
  } = props

  const affixRef = useRef<HTMLDivElement>(null)
  const { y } = useWindowScroll()
  const { width: windowWidth } = useWindowSize()
  const [baseElement, setBaseElement] = useState<HTMLDivElement | null>()
  const [initWindowSize] = useState(windowWidth)
  const [affix, setAffix] = useState(false)
  const [limit, setLimit] = useState<number | undefined>()
  const [forceTop, setForceTop] = useState<number | undefined>()
  const [affixProps, setAffixProps] = useState({
    assigned: false,
    offset: 0,
    height: 0,
    width: 0,
    top: 0,
    left: 0,
    leftOffset: 0,
  })
  const scrollbarWidth = (useScrollbarWidth() || 0) / 2

  useEffect(() => {
    setBaseElement(target ? document.querySelector<HTMLDivElement>(target) : affixRef.current)
  }, [target])

  useEffect(() => {
    if (!scrollableAreaSelector) return

    const scrollableArea = document.querySelector<HTMLDivElement>(scrollableAreaSelector)
    const scrollableAreaHeight = scrollableArea?.offsetHeight || 0
    const scrollableAreaTop = scrollableArea?.offsetTop || 0

    setLimit(scrollableAreaHeight + scrollableAreaTop - affixProps.height - affixProps.offset)
  }, [scrollableAreaSelector, affixProps.height, affixProps.offset])

  useEffect(() => {
    if (!baseElement) return

    setAffixProps((prev) => ({
      ...prev,
      height: baseElement.offsetHeight,
      width: baseElement.offsetWidth,
      top: baseElement.offsetTop,
      leftOffset: baseElement.offsetLeft + scrollbarWidth - initWindowSize / 2,
      assigned: true,
      offset,
    }))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseElement, offset])

  useEffect(() => {
    if (!enabled || !affixProps.assigned) return

    const shouldShowAffix = y >= affixProps.top - offset

    if (shouldShowAffix && !affix) {
      setAffix(true)
    }

    if (!shouldShowAffix && affix) {
      setAffix(false)
    }

    if (limit && y >= limit && affix) {
      setForceTop(limit - y)
    }

    if (limit && y < limit && affix) {
      setForceTop(undefined)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [y, limit])

  return (
    <AffixWrapper ref={affixRef} affix={affix} forceTop={forceTop} {...affixProps}>
      <AffixContainer
        className={classNames(idleClassName, affix && activeClassName)}
        style={forceTop !== undefined ? { transform: `translateY(${forceTop}px)` } : {}}
      >
        {children}
      </AffixContainer>
    </AffixWrapper>
  )
}

Affix.displayName = 'Affix'
Affix.propTypes = {
  offset: PropTypes.number,
  enabled: PropTypes.bool,
  idleClassName: PropTypes.string,
  activeClassName: PropTypes.string,
  target: PropTypes.string,
  scrollableAreaSelector: PropTypes.string,
}
Affix.defaultProps = {
  offset: 0,
  enabled: true,
  idleClassName: 'affix',
  activeClassName: 'affix-active',
  target: undefined,
  scrollableAreaSelector: undefined,
}

export { Affix }
